import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, Plus, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MediaEmbed } from "../components/MediaEmbed";

const EMPTY = { name: "", description: "", type: "tshirt", category: "punk", price: 30, image: "", images: [], video: "", sizes: "S,M,L,XL", colors: "Black,White", stock: 100, featured: false };

export default function Admin() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [siteSettings, setSiteSettings] = useState({ hero_video: "", promo_video: "", promo_title: "", promo_text: "" });
  const [savingSettings, setSavingSettings] = useState(false);

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingVideo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, video: data.url }));
      toast.success("Video uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploadingVideo(false);
      e.target.value = "";
    }
  };

  const uploadSetting = async (e, key) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSiteSettings((s) => ({ ...s, [key]: data.url }));
      toast.success("Uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      e.target.value = "";
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put("/settings", siteSettings);
      toast.success("Media saved");
    } catch (err) {
      toast.error("Save failed");
    } finally {
      setSavingSettings(false);
    }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(data.url);
      }
      setForm((f) => {
        const images = [...(f.images || []), ...urls];
        return { ...f, images, image: f.image || images[0] };
      });
      toast.success(`${urls.length} image${urls.length > 1 ? "s" : ""} uploaded`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (idx) => {
    setForm((f) => {
      const images = (f.images || []).filter((_, i) => i !== idx);
      return { ...f, images, image: images[0] || "" };
    });
  };

  const setCover = (idx) => {
    setForm((f) => ({ ...f, image: (f.images || [])[idx] || f.image }));
  };

  useEffect(() => {
    if (user === false || (user && user.role !== "admin")) nav("/");
  }, [user, nav]);

  const load = () => {
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSiteSettings(r.data)).catch(() => {});
  };
  useEffect(() => { if (user?.role === "admin") load(); }, [user]);

  if (!user || user.role !== "admin") return <div className="max-w-[1400px] mx-auto px-5 py-20 font-mono">Loading_</div>;

  const openNew = () => { setEditing("new"); setForm(EMPTY); };
  const openEdit = (p) => {
    setEditing(p.id);
    const images = p.images && p.images.length ? p.images : (p.image ? [p.image] : []);
    setForm({ ...p, images, sizes: (p.sizes || []).join(","), colors: (p.colors || []).join(",") });
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.image) return toast.error("Add a product image first");
    const payload = {
      ...form,
      images: form.images || [],
      image: form.image || (form.images || [])[0] || "",
      price: parseFloat(form.price),
      stock: parseInt(form.stock) || 0,
      sizes: form.sizes ? form.sizes.split(",").map((s) => s.trim()).filter(Boolean) : [],
      colors: form.colors ? form.colors.split(",").map((s) => s.trim()).filter(Boolean) : [],
    };
    delete payload.id;
    delete payload.created_at;
    try {
      if (editing === "new") await api.post("/products", payload);
      else await api.put(`/products/${editing}`, payload);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Deleted");
    load();
  };

  const setStatus = async (id, status) => {
    await api.put(`/admin/orders/${id}`, { status });
    toast.success("Order updated");
    load();
  };

  const inputCls = "w-full border-2 border-border px-3 py-2 font-mono text-sm focus:outline-none focus:border-primary";

  return (
    <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-12 md:py-16" data-testid="admin-page">
      <h1 className="font-anton text-5xl md:text-7xl uppercase tracking-tighter mb-8">Admin</h1>

      <div className="flex gap-3 mb-8 border-b-2 border-border">
        {["products", "orders", "media"].map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`} className={`font-outfit font-bold uppercase text-sm tracking-widest px-5 py-3 border-2 border-b-0 -mb-0.5 ${tab === t ? "bg-black text-white border-border" : "bg-card border-transparent"}`}>
            {t}
          </button>
        ))}
      </div>

      {tab === "products" && (
        <div data-testid="admin-products">
          <button onClick={openNew} data-testid="admin-add-product" className="mb-6 inline-flex items-center gap-2 bg-primary text-primary-foreground font-outfit font-bold uppercase text-xs tracking-widest px-5 py-3 border-2 border-primary hover:bg-secondary hover:border-secondary transition-colors">
            <Plus size={16} /> Add Product
          </button>
          <div className="border-2 border-border overflow-x-auto">
            <table className="w-full font-mono text-sm">
              <thead>
                <tr className="bg-black text-white text-left uppercase text-xs tracking-widest">
                  <th className="p-3">Item</th><th className="p-3">Type</th><th className="p-3">Vibe</th><th className="p-3">Price</th><th className="p-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} className="border-t-2 border-border" data-testid={`admin-product-${p.id}`}>
                    <td className="p-3 flex items-center gap-3"><img src={p.image} alt="" className="w-10 h-12 object-cover border border-border" /><span className="font-bold uppercase">{p.name}</span></td>
                    <td className="p-3">{p.type}</td>
                    <td className="p-3 uppercase">{p.category}</td>
                    <td className="p-3">${p.price.toFixed(2)}</td>
                    <td className="p-3">
                      <div className="flex gap-2 justify-end">
                        <button onClick={() => openEdit(p)} data-testid={`admin-edit-${p.id}`} className="p-2 border-2 border-border hover:bg-primary hover:text-black"><Pencil size={14} /></button>
                        <button onClick={() => del(p.id)} data-testid={`admin-delete-${p.id}`} className="p-2 border-2 border-border hover:bg-secondary hover:border-secondary hover:text-white hover:text-white"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "orders" && (
        <div data-testid="admin-orders" className="space-y-4">
          {orders.length === 0 && <p className="font-mono text-sm text-muted-foreground">No orders yet.</p>}
          {orders.map((o) => (
            <div key={o.id} className="border-2 border-border p-4" data-testid={`admin-order-${o.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <span className="font-mono text-xs uppercase tracking-widest">#{o.id.slice(-6)} · {o.user_email}</span>
                <span className="font-mono text-xs text-muted-foreground">{o.shipping_name}, {o.shipping_city}</span>
                <span className="font-anton text-lg">${o.total.toFixed(2)}</span>
                <select value={o.status} onChange={(e) => setStatus(o.id, e.target.value)} data-testid={`admin-order-status-${o.id}`} className="border-2 border-border px-2 py-1 font-mono text-xs uppercase">
                  {["processing", "printing", "shipped", "delivered"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "media" && (
        <div data-testid="admin-media" className="max-w-2xl space-y-8">
          <p className="font-mono text-xs text-muted-foreground">Add short looping clips (upload MP4 or paste a YouTube link). Videos autoplay muted on the storefront.</p>
          <div className="border-2 border-border p-5">
            <h3 className="font-outfit font-bold uppercase tracking-widest text-sm mb-3">Homepage Hero Video</h3>
            {siteSettings.hero_video && (
              <div className="mb-3 aspect-video bg-black border border-border overflow-hidden">
                <MediaEmbed url={siteSettings.hero_video} className="w-full h-full object-cover" controls />
              </div>
            )}
            <input type="file" accept="video/mp4,video/webm,video/quicktime" onChange={(e) => uploadSetting(e, "hero_video")} data-testid="hero-video-file" className="block w-full text-xs font-mono text-muted-foreground file:mr-3 file:py-2 file:px-3 file:border-2 file:border-border file:bg-primary file:text-primary-foreground file:font-bold file:uppercase file:cursor-pointer" />
            <input placeholder="…or paste YouTube / video URL" value={siteSettings.hero_video || ""} onChange={(e) => setSiteSettings({ ...siteSettings, hero_video: e.target.value })} data-testid="hero-video-url" className={`${inputCls} mt-3`} />
            {siteSettings.hero_video && <button type="button" onClick={() => setSiteSettings({ ...siteSettings, hero_video: "" })} className="mt-2 font-mono text-[10px] uppercase text-primary">Remove</button>}
          </div>
          <div className="border-2 border-border p-5">
            <h3 className="font-outfit font-bold uppercase tracking-widest text-sm mb-3">Promo Section Video</h3>
            {siteSettings.promo_video && (
              <div className="mb-3 aspect-video bg-black border border-border overflow-hidden">
                <MediaEmbed url={siteSettings.promo_video} className="w-full h-full object-cover" controls />
              </div>
            )}
            <input type="file" accept="video/mp4,video/webm,video/quicktime" onChange={(e) => uploadSetting(e, "promo_video")} data-testid="promo-video-file" className="block w-full text-xs font-mono text-muted-foreground file:mr-3 file:py-2 file:px-3 file:border-2 file:border-border file:bg-primary file:text-primary-foreground file:font-bold file:uppercase file:cursor-pointer" />
            <input placeholder="…or paste YouTube / video URL" value={siteSettings.promo_video || ""} onChange={(e) => setSiteSettings({ ...siteSettings, promo_video: e.target.value })} data-testid="promo-video-url" className={`${inputCls} mt-3`} />
            <input placeholder="Promo title" value={siteSettings.promo_title || ""} onChange={(e) => setSiteSettings({ ...siteSettings, promo_title: e.target.value })} data-testid="promo-title" className={`${inputCls} mt-3`} />
            <textarea placeholder="Promo text" value={siteSettings.promo_text || ""} onChange={(e) => setSiteSettings({ ...siteSettings, promo_text: e.target.value })} data-testid="promo-text" rows={2} className={`${inputCls} mt-3`} />
            {siteSettings.promo_video && <button type="button" onClick={() => setSiteSettings({ ...siteSettings, promo_video: "" })} className="mt-2 font-mono text-[10px] uppercase text-primary">Remove</button>}
          </div>
          <button onClick={saveSettings} disabled={savingSettings} data-testid="save-media" className="bg-primary text-primary-foreground font-outfit font-bold uppercase tracking-widest text-sm px-6 py-3 border-2 border-primary hover:bg-secondary hover:border-secondary hover:text-white transition-colors disabled:opacity-50">
            {savingSettings ? "Saving…" : "Save Media"}
          </button>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="admin-modal">
          <form onSubmit={save} className="bg-card border-2 border-border w-full max-w-lg max-h-[90vh] overflow-auto p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-anton text-3xl uppercase tracking-tighter">{editing === "new" ? "New Product" : "Edit Product"}</h2>
              <button type="button" onClick={() => setEditing(null)} data-testid="admin-modal-close"><X size={22} /></button>
            </div>
            <div className="grid gap-3">
              <input required placeholder="NAME" data-testid="form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
              <textarea placeholder="DESCRIPTION" data-testid="form-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} rows={2} />
              <div className="border-2 border-border p-3">
                <label className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Product Images {form.images?.length ? `(${form.images.length})` : ""}</label>
                {form.images && form.images.length > 0 ? (
                  <div className="mt-2 grid grid-cols-3 gap-2" data-testid="form-image-gallery">
                    {form.images.map((img, idx) => (
                      <div key={idx} className={`relative group border-2 ${form.image === img ? "border-primary" : "border-border"}`}>
                        <img src={img} alt={`img-${idx}`} className="w-full h-24 object-cover" data-testid={idx === 0 ? "form-image-preview" : `form-image-${idx}`} />
                        {form.image === img && <span className="absolute top-0 left-0 bg-primary text-primary-foreground font-mono text-[9px] uppercase px-1">Cover</span>}
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          {form.image !== img && (
                            <button type="button" onClick={() => setCover(idx)} data-testid={`set-cover-${idx}`} className="font-mono text-[9px] uppercase bg-white text-black px-2 py-1">Cover</button>
                          )}
                          <button type="button" onClick={() => removeImage(idx)} data-testid={`remove-image-${idx}`} className="font-mono text-[9px] uppercase bg-primary text-primary-foreground px-2 py-1">Remove</button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="mt-2 w-full h-32 border border-dashed border-border flex items-center justify-center font-mono text-xs text-muted-foreground">No images yet</div>
                )}
                <input type="file" multiple accept="image/png,image/jpeg,image/gif,image/webp" onChange={handleUpload} data-testid="form-image-file" className="mt-3 block w-full text-xs font-mono text-muted-foreground file:mr-3 file:py-2 file:px-3 file:border-2 file:border-border file:bg-primary file:text-primary-foreground file:font-bold file:uppercase file:cursor-pointer file:rounded-none" />
                <p className="mt-1 font-mono text-[10px] text-muted-foreground">Pick one or several — first upload becomes the cover. Tap a photo to change cover or remove.</p>
                {uploading && <p className="mt-2 font-mono text-xs text-primary animate-pulse">Uploading…</p>}
                <input placeholder="…or paste an image URL" data-testid="form-image" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value, images: form.images?.length ? form.images : (e.target.value ? [e.target.value] : []) })} className={`${inputCls} mt-3`} />
              </div>
              <div className="border-2 border-border p-3">
                <label className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Product Video (optional · 5-10s loop)</label>
                {form.video && (
                  <div className="mt-2 aspect-video bg-black border border-border overflow-hidden" data-testid="form-video-preview">
                    <MediaEmbed url={form.video} className="w-full h-full object-cover" controls />
                  </div>
                )}
                <input type="file" accept="video/mp4,video/webm,video/quicktime" onChange={handleVideoUpload} data-testid="form-video-file" className="mt-3 block w-full text-xs font-mono text-muted-foreground file:mr-3 file:py-2 file:px-3 file:border-2 file:border-border file:bg-primary file:text-primary-foreground file:font-bold file:uppercase file:cursor-pointer file:rounded-none" />
                {uploadingVideo && <p className="mt-2 font-mono text-xs text-primary animate-pulse">Uploading video…</p>}
                <input placeholder="…or paste a YouTube / video URL" data-testid="form-video" value={form.video || ""} onChange={(e) => setForm({ ...form, video: e.target.value })} className={`${inputCls} mt-3`} />
                {form.video && <button type="button" onClick={() => setForm({ ...form, video: "" })} data-testid="remove-video" className="mt-2 font-mono text-[10px] uppercase text-primary">Remove video</button>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="form-type" className={inputCls}>
                  <option value="tshirt">T-Shirt</option><option value="print">Print</option>
                </select>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value, ...(e.target.value === "print" ? { type: "print" } : {}) })} data-testid="form-category" className={inputCls}>
                  <option value="punk">Punk</option><option value="sports">Sports</option><option value="fitness">Fitness</option><option value="street">Street Gear</option><option value="print">Prints</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input type="number" step="0.01" placeholder="PRICE" data-testid="form-price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={inputCls} />
                <input type="number" placeholder="STOCK" data-testid="form-stock" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className={inputCls} />
              </div>
              <input placeholder="SIZES (comma separated)" data-testid="form-sizes" value={form.sizes} onChange={(e) => setForm({ ...form, sizes: e.target.value })} className={inputCls} />
              <input placeholder="COLORS (comma separated)" data-testid="form-colors" value={form.colors} onChange={(e) => setForm({ ...form, colors: e.target.value })} className={inputCls} />
              <label className="flex items-center gap-2 font-mono text-sm uppercase">
                <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="form-featured" className="w-4 h-4" /> Featured on homepage
              </label>
            </div>
            <button type="submit" data-testid="form-save" className="mt-5 w-full bg-primary text-primary-foreground font-outfit font-bold uppercase tracking-widest text-sm py-3 border-2 border-border hover:bg-secondary hover:border-secondary hover:text-white transition-colors">Save Product</button>
          </form>
        </div>
      )}
    </div>
  );
}
